import type { PropsWithChildren } from 'react';
import Collapse from 'react-collapse';
import styled from 'styled-components';

import {
  mediaConfined,
  mediaConfinedNavlessOnly,
  mediaWideNavless,
} from '@xing-com/layout-tokens';
import {
  scale110,
  scale190,
  scale220,
  scale250,
  spaceS,
  spaceM,
  spaceL,
  spaceXL,
  spaceXXL,
  space4XL,
  xdlColorBorderSoft,
  xdlPaletteRed10,
  space3XL,
} from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

const GroupHeader = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: ${spaceXL};
`;

const GroupTableHeader = styled.div`
  border-bottom: 1px solid ${xdlColorBorderSoft};
  column-gap: ${spaceM};
  display: grid;
  padding-bottom: ${spaceXL};

  @media ${mediaWideNavless} {
    padding-bottom: ${spaceXXL};
  }

  @media ${mediaConfinedNavlessOnly} {
    column-gap: ${spaceXXL};
  }
`;

const GroupContainer = styled.div`
  margin-bottom: ${space4XL};
`;

const GroupContentContainer = styled.div`
  display: flex;
  margin-top: ${spaceXL};

  @media ${mediaConfined} {
    margin-top: ${spaceXXL};
  }
`;

const GroupImage = styled.div`
  height: ${scale190};
  margin-right: ${spaceL};
  min-width: ${scale190};

  & img {
    height: 100%;
  }

  @media ${mediaConfinedNavlessOnly} {
    height: ${scale220};
    margin-right: ${spaceXL};
    min-width: ${scale220};
  }

  @media ${mediaWideNavless} {
    height: ${scale250};
    margin-left: ${space3XL};
    margin-right: 0;
    margin-right: calc(${space4XL} + ${spaceM});
    margin-top: ${spaceL};
    min-width: ${scale250};
  }
`;

const GroupContent = styled.div`
  width: 100%;
`;

const GroupHeaderContainer = styled.div`
  @media ${mediaConfined} {
    margin-bottom: ${spaceL};
  }
`;

const GroupHeaderHeadline = styled(Headline)`
  column-gap: ${spaceM};
  display: grid;

  @media ${mediaConfinedNavlessOnly} {
    column-gap: ${spaceXXL};
  }
`;

// The spacings in grid-template-columns are based on the width the
// "Basic" and "Premium" words take. This is calculated regardless,
// but it's here to feel more seemless whenever possible.
const Feature = styled.div`
  column-gap: ${spaceM};
  display: grid;
  grid-template-columns: 1fr 34px 57px;

  @media ${mediaConfinedNavlessOnly} {
    column-gap: ${spaceXXL};
    grid-template-columns: 1fr 39px 66px;
  }
`;

const FeatureText = styled(BodyCopy)`
  margin-bottom: ${spaceS};
`;

const FeatureSpecialText = styled.span`
  color: ${xdlPaletteRed10};
  font-weight: 700;
`;

const FeatureValue = styled(BodyCopy)`
  align-items: flex-start;
  display: flex;
  justify-content: center;

  & svg {
    height: ${scale110};
    width: ${scale110};
  }
`;

const TipsAndTricksCollapseContainer = styled(
  // oxlint-disable-next-line typescript/consistent-type-assertions
  Collapse as unknown as React.ComponentType<
    PropsWithChildren<{ isOpened: boolean }>
  >
)`
  margin-top: ${spaceXL};
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const FeatureListElement = styled.li`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Styled = {
  GroupHeader,
  GroupTableHeader,
  GroupContainer,
  GroupContentContainer,
  GroupImage,
  GroupContent,
  GroupHeaderContainer,
  GroupHeaderHeadline,
  Feature,
  FeatureText,
  FeatureSpecialText,
  FeatureValue,
  FeatureList,
  FeatureListElement,
  TipsAndTricksCollapseContainer,
};
